import React from "react";
/* import { Card } from "../../features/ui/card"; */
import { Skeleton } from "../../features/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../features/ui/table";

interface CustomerLoaderProps {
    variant?: "customer-chart" | "segmentation-tbl" | "purchase-freq" | "cohort" |
    "region-analysis1" | "region-analysis2" | "life-value1" | "life-value2" | "customer-pie" | "customer-stats";
    className?: string;
    itemCount?: number; // Optional: customizable item count
}


const CustomerLoader: React.FC<CustomerLoaderProps> = ({ variant = "list", className = "" }) => {
    switch (variant) {
        case "customer-chart":
            return (
                <div className={className} aria-busy="true" role="status">
                    <div className="flex flex-row px-6 pt-6 space-x-4">
                        {/* Y-axis bar */}
                        <Skeleton className="h-70 w-6 rounded-lg bg-secondary" />
                        {/* Simulated chart area */}
                        <Skeleton className="h-60 w-full rounded-lg bg-secondary" />
                    </div>
                    {/* Bottom axis labels */}
                    <div className="px-6 pt-4 pb-4">
                        <Skeleton className="h-4 w-full rounded-md bg-secondary" />
                    </div>
                    <div className="flex justify-center space-x-2 mb-4  ">
                        <Skeleton className="h-6 w-14 bg-secondary" />
                        <Skeleton className="h-6 w-14 bg-secondary" />
                    </div>
                    <div className="flex justify-center space-x-12 mb-4  ">
                        <Skeleton className="h-24 w-24 bg-secondary" />
                        <Skeleton className="h-24 w-24 bg-secondary" />
                        <Skeleton className="h-24 w-24 bg-secondary" />
                    </div>

                </div>
            )
        case "segmentation-tbl":
            return (
                <div className={className} aria-busy="true" role="status">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Segment</TableHead>
                                <TableHead>Customers</TableHead>
                                <TableHead>Percentage</TableHead>
                                <TableHead>Avg. Order Value</TableHead>
                                <TableHead>Purchase Frequency</TableHead>
                                <TableHead>Status</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {[...Array(6)].map((_, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    <TableCell><Skeleton className="h-4 w-20 bg-secondary" /></TableCell>
                                    <TableCell><Skeleton className="h-4 w-20 bg-secondary" /></TableCell>
                                    <TableCell><Skeleton className="h-4 w-12 bg-secondary" /></TableCell>
                                    <TableCell><Skeleton className="h-4 w-14 bg-secondary" /></TableCell>
                                    <TableCell><Skeleton className="h-4 w-16 bg-secondary" /></TableCell>
                                    <TableCell><Skeleton className="h-4 w-16 bg-secondary" /></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )
        case "purchase-freq":
            return (
                <div className={className} aria-busy="true" role="status">
                    <div className="flex flex-row justify-between space-x-6 m-4">
                        {/* Left Chart Area */}
                        <div className="flex-1 px-6 pt-6">
                            <div className="flex flex-row space-x-4">
                                {/* Y-axis bar */}
                                <Skeleton className="h-70 w-6 rounded-lg bg-secondary" />
                                {/* Simulated chart area */}
                                <Skeleton className="h-60 w-full rounded-lg bg-secondary" />
                            </div>
                            {/* Bottom axis labels */}
                            <div className="pt-4 pb-4">
                                <Skeleton className="h-4 w-full rounded-md bg-secondary" />
                            </div>
                        </div>

                        {/* Right Donut Chart */}
                        <div className="flex justify-center items-center h-[310px] pr-6">
                            <div className="relative">
                                <Skeleton className="w-[200px] h-[200px] rounded-full bg-secondary" />
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-center space-x-12 mb-4  ">
                        <Skeleton className="h-24 w-24 bg-secondary" />
                        <Skeleton className="h-24 w-24 bg-secondary" />
                        <Skeleton className="h-24 w-24 bg-secondary" />
                    </div>

                </div>
            )
        case "cohort":
            return (
                <div className={className} aria-busy="true" role="status">
                    <div className="rounded-md border overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Cohort</TableHead>
                                    <TableHead className="text-center">Month 1</TableHead>
                                    <TableHead className="text-center">Month 2</TableHead>
                                    <TableHead className="text-center">Month 3</TableHead>
                                    <TableHead className="text-center">Month 4</TableHead>
                                    <TableHead className="text-center">Month 5</TableHead>
                                    <TableHead className="text-center">Month 6</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {Array.from({ length: 6 }).map((_, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <Skeleton className="h-4 w-24 rounded bg-secondary" />
                                        </TableCell>
                                        {Array.from({ length: 6 }).map((_, mIdx) => (
                                            <TableCell className="text-center" key={mIdx}>
                                                <Skeleton className="h-4 w-12 mx-auto rounded bg-secondary" />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                    <div className="mt-4 p-4 bg-muted/30 rounded-lg">
                        <Skeleton className="h-5 w-1/4 mb-3 rounded bg-secondary" />
                        <ul className="space-y-2">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <li key={i}>
                                    <Skeleton className="h-4 w-full max-w-md rounded bg-secondary" />
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

            )
        case "region-analysis1":
            return (
                <div className={className} aria-busy="true" role="status">
                    <div className="flex flex-row px-6  space-x-4">
                        {/* Y-axis bar */}
                        <Skeleton className="h-60 w-6 rounded-lg bg-secondary" />
                        {/* Simulated chart area */}
                        <Skeleton className="h-50 w-full rounded-lg bg-secondary" />
                    </div>
                    {/* Bottom axis labels */}
                    <div className="px-6 pt-4 pb-2">
                        <Skeleton className="h-4 w-full rounded-md bg-secondary" />
                    </div>
                    <div className="flex justify-center space-x-2 mb-4  ">
                        <Skeleton className="h-6 w-14 bg-secondary" />

                    </div>

                </div>
            )
        case "region-analysis2":
            return (
                <div className={className} aria-busy="true" role="status">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Region</TableHead>
                                <TableHead className="text-right">Customers</TableHead>
                                <TableHead className="text-right">Sales</TableHead>
                                <TableHead className="text-right">Avg. Order Value</TableHead>
                                <TableHead className="text-right">% of Total</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Array.from({ length: 6 }).map((_, i) => (
                                <TableRow key={i}>
                                    <TableCell>
                                        <Skeleton className="h-4 w-24 rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Skeleton className="h-4 w-14 ml-auto rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Skeleton className="h-4 w-16 ml-auto rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Skeleton className="h-4 w-20 ml-auto rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Skeleton className="h-4 w-12 ml-auto rounded bg-secondary" />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )
        case "life-value1":
            return (
                <div className={className} aria-busy="true" role="status">
                    <div className="flex flex-row px-6  space-x-4">
                        {/* Y-axis bar */}
                        <Skeleton className="h-60 w-6 rounded-lg bg-secondary" />
                        {/* Simulated chart area */}
                        <Skeleton className="h-50 w-full rounded-lg bg-secondary" />
                    </div>
                    {/* Bottom axis labels */}
                    <div className="px-6 pt-4 pb-2">
                        <Skeleton className="h-4 w-full rounded-md bg-secondary" />
                    </div>
                    <div className="flex justify-center space-x-2 mb-4  ">
                        <Skeleton className="h-6 w-14 bg-secondary" />
                        <Skeleton className="h-6 w-14 bg-secondary" />
                    </div>

                </div>
            )
        case "life-value2":
            return (
                <div className={className} aria-busy="true" role="status">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Segment</TableHead>
                                <TableHead className="text-right">CLV</TableHead>
                                <TableHead className="text-right">Acquisition Cost</TableHead>
                                <TableHead className="text-right">ROI</TableHead>
                                <TableHead className="text-right">CLV:CAC Ratio</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Array.from({ length: 6 }).map((_, i) => (
                                <TableRow key={i}>
                                    <TableCell>
                                        <Skeleton className="h-4 w-24 rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Skeleton className="h-4 w-20 ml-auto rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Skeleton className="h-4 w-24 ml-auto rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Skeleton className="h-4 w-10 ml-auto rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Skeleton className="h-4 w-16 ml-auto rounded bg-secondary" />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )
        case "customer-pie":
            return (
                <div className={className} aria-busy="true" role="status">
                        <div className="h-[300px]">
                            <div className="flex justify-center items-center h-[90%]">
                                <Skeleton className="w-[160px] h-[160px] rounded-full bg-secondary" />
                            </div>
                        </div>               
                </div>
            )
        case "customer-stats":
            return(
                <div className={className} aria-busy="true" role="status">                                         
                        <ul className="space-y-2">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <li key={i}>
                                    <Skeleton className="h-4 w-full max-w-md rounded bg-secondary" />
                                </li>
                            ))}
                        </ul>                   
                </div>
            )    
    }

}

export default CustomerLoader